import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { LockOutlined as LockOutlinedIcon, Payments as PaymentsIcon } from '@mui/icons-material';
import { tienePermisosModulo, puedeEditarProgramaContabilidad } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';
import PrintListado from 'utils/PrintListado';
import tiposDocumentosJson from 'utils/tiposDocumentos.json';

const PROGRAMA = 'aplicacioncobrossuplidor';
const TIPO_DOCUMENTO_MOTOR_PAGO_SUPLIDORES = 'Pagos a Suplidores';
const DOCUMENTO_PAGO_CXP = 'PAGO CXP';

const moduloContabilidadInstalado = (modulos) => {
  if (modulos == null || modulos === false || modulos === 'false') return false;
  const partes = Array.isArray(modulos) ? modulos : String(modulos).split(/[,;]/);
  return partes.some((m) => String(m || '').trim().toLowerCase() === 'contabilidad');
};

const bloqueMotorDeTipo = (tipooperacion) => {
  const clave = String(tipooperacion || '').toLowerCase().replace(/\s/g, '');
  if (!clave) return null;
  return tiposDocumentosJson.find((e) => e?.[clave])?.[clave] || null;
};

const origenFormaPagoReglas = (tipooperacion) => {
  const bloque = bloqueMotorDeTipo(tipooperacion);
  if (!bloque) return [];
  const raw = bloque.origenformapago ?? bloque.origenformadepago ?? [];
  return (Array.isArray(raw) ? raw : [raw]).map((x) => String(x || '').trim()).filter(Boolean);
};

const tipoTieneOrigenFormaPago = (tipooperacion) => origenFormaPagoReglas(tipooperacion).length > 0;

const payloadFormaPagoSiAplica = (tipoDocumento, documentoFormaPago) => {
  const tipo = String(tipoDocumento || '').trim();
  const documento = String(documentoFormaPago || '').trim();
  if (!tipo || !tipoTieneOrigenFormaPago(tipo) || !documento) return {};
  return { forma_pago: documento };
};

const codigoUsaOrigenFormaPago = (tipooperacion, codigoRegla) => {
  const codigo = String(codigoRegla || '').trim().toLowerCase();
  if (!codigo) return false;
  return origenFormaPagoReglas(tipooperacion).some((c) => c.toLowerCase() === codigo);
};

const documentoMotorDefinido = (registros, tipoDocumento) => {
  const tipo = String(tipoDocumento || '').trim();
  if (!tipo) return false;
  return (registros || []).some((r) => {
    if (String(r?.tipo_documento || '').trim() !== tipo) return false;
    if (codigoUsaOrigenFormaPago(tipo, r?.codigo_regla)) return true;
    const aplica = String(r?.aplica_a || 'cuenta').trim().toLowerCase();
    if (aplica === 'json') return true;
    return Boolean(String(r?.cuentacontable || '').trim());
  });
};

const reglasMotorDeDocumento = (registros, tipoDocumento) => {
  const tipo = String(tipoDocumento || '').trim();
  return (registros || []).filter((r) => String(r?.tipo_documento || '').trim() === tipo);
};

const CLAVES_META_MOTOR = new Set([
  'tipodocumento',
  'nfactura',
  'documento',
  'numero',
  'suplidor',
  'fecha',
  'tasa',
  'usuario',
  'concepto',
  'valor',
  'valor_total',
  'lineas',
  'cuentas',
]);

const redondearDinero2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const construirDocumentoMotorPagoSuplidor = (reglas, ctx) => {
  const reglaFp = (reglas || []).find((r) =>
    codigoUsaOrigenFormaPago(TIPO_DOCUMENTO_MOTOR_PAGO_SUPLIDORES, r?.codigo_regla)
  );
  const cuenta = String(
    reglaFp?.cuentacontable || ctx.formaPagoRow?.cuenta_contable || ''
  ).trim();
  const valor = redondearDinero2(ctx.valorTotal);
  const lineasFp = cuenta && valor > 0 ? [{ cuenta, monto: valor }] : [];
  const montos = ctx.montos || {};
  const doc = {
    tipodocumento: TIPO_DOCUMENTO_MOTOR_PAGO_SUPLIDORES,
    nfactura: ctx.nfactura,
    documento: ctx.documento,
    numero: ctx.numero,
    suplidor: ctx.suplidor,
    fecha: ctx.fecha,
    tasa: ctx.tasa,
    usuario: ctx.usuario,
    concepto: ctx.concepto,
    valor,
    lineas: lineasFp,
  };
  (reglas || []).forEach((regla) => {
    const codigo = String(regla?.codigo_regla || '').trim();
    if (!codigo || CLAVES_META_MOTOR.has(codigo.toLowerCase())) return;
    const usaFp =
      codigoUsaOrigenFormaPago(TIPO_DOCUMENTO_MOTOR_PAGO_SUPLIDORES, codigo) ||
      String(regla?.aplica_a || '').trim().toLowerCase() === 'json';
    if (usaFp) {
      doc[codigo] = lineasFp;
      return;
    }
    const extra = montos[codigo] ?? montos[codigo.toLowerCase()];
    doc[codigo] = redondearDinero2(extra != null ? extra : valor);
  });
  return doc;
};

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const opcionLabel = (s) => {
  if (!s) return '';
  const id = s.id != null ? String(s.id) : '';
  const d = (s.descrip || '').trim();
  const r = (s.rnc || '').trim();
  return `${id}-${d}-${r}`;
};

const fechaLocalHoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatearFecha = (fecha) => {
  if (!fecha) return '-';
  try {
    const d = new Date(fecha);
    return d.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return fecha;
  }
};

const tramoLabel = (t) => {
  if (t === '90+') return 'Más de 90 días';
  return `${t} días`;
};

const AplicacionCobrosSuplidor = () => {
  const { controller, selectedCompany, showSuccess, showError, showWarning } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles, modulos } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA);
  const puedeModificar = puedeEditarProgramaContabilidad(roleDetalles, PROGRAMA);
  const puedeOperarDb = Boolean(selectedCompany?.db_empresa);

  const [suplidores, setSuplidores] = useState([]);
  const [suplidorSel, setSuplidorSel] = useState(null);
  const [filas, setFilas] = useState([]);
  const [cargandoSupl, setCargandoSupl] = useState(false);
  const [cargandoFacturas, setCargandoFacturas] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [formasPagoDb, setFormasPagoDb] = useState([]);
  const [cargandoFormas, setCargandoFormas] = useState(false);

  const [fechaPago, setFechaPago] = useState(fechaLocalHoy());
  const [formaPago, setFormaPago] = useState('');
  const [referencia, setReferencia] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [montoPago, setMontoPago] = useState('');

  const cargarSuplidores = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb) return;
    setCargandoSupl(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarsuplidores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudieron cargar suplidores');
        setSuplidores([]);
        return;
      }
      setSuplidores(data.suplidores || []);
    } catch {
      setSuplidores([]);
      showError('Error de conexión al cargar suplidores');
    } finally {
      setCargandoSupl(false);
    }
  }, [puedeLeer, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  const cargarFormasPago = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb) return;
    setCargandoFormas(true);
    try {
      const response = await fetch(`${servidor}/configuracionbackend_listarformasdepago`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setFormasPagoDb([]);
        showError(data.Error || 'No se pudo cargar formas de pago');
        return;
      }
      setFormasPagoDb(data.formas_pago || []);
    } catch {
      setFormasPagoDb([]);
      showError('Error de conexión al cargar formas de pago');
    } finally {
      setCargandoFormas(false);
    }
  }, [puedeLeer, puedeOperarDb, servidor, selectedCompany, usuarioLog, claveLog, showError]);

  const cargarFacturasPendientes = useCallback(async () => {
    if (!puedeLeer || !puedeOperarDb || !suplidorSel?.id) return;
    setCargandoFacturas(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_listarcuentasxpagar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          suplidor_id: String(suplidorSel.id),
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo cargar facturas pendientes');
        setFilas([]);
        return;
      }
      const lista = (data.facturas || []).map((f, idx) => ({
        ...f,
        keyLocal: `${f.nfactura || 'NF'}-${idx}`,
        aplicar: '',
      }));
      setFilas(lista);
      if (lista.length === 0) showSuccess('No hay facturas pendientes para este suplidor');
    } catch {
      setFilas([]);
      showError('Error de conexión al cargar facturas');
    } finally {
      setCargandoFacturas(false);
    }
  }, [puedeLeer, puedeOperarDb, servidor, suplidorSel, selectedCompany, usuarioLog, claveLog, showError, showSuccess]);

  useEffect(() => {
    if (puedeLeer && puedeOperarDb) {
      cargarSuplidores();
      cargarFormasPago();
    }
  }, [puedeLeer, puedeOperarDb, cargarSuplidores, cargarFormasPago]);

  useEffect(() => {
    if (formasPagoDb.length > 0 && !formaPago) {
      setFormaPago(formasPagoDb[0].documento || '');
    }
  }, [formasPagoDb, formaPago]);

  useEffect(() => {
    if (!suplidorSel?.id) {
      setFilas([]);
      return;
    }
    cargarFacturasPendientes();
  }, [suplidorSel, cargarFacturasPendientes]);

  const setAplicarFila = (keyLocal, valorStr) => {
    setFilas((prev) =>
      prev.map((r) => {
        if (r.keyLocal !== keyLocal) return r;
        return { ...r, aplicar: valorStr };
      })
    );
  };

  const totalAplicar = useMemo(
    () =>
      filas.reduce((s, r) => {
        const n = parseFloat(String(r.aplicar || '').replace(',', '.'));
        return s + (Number.isFinite(n) ? n : 0);
      }, 0),
    [filas]
  );

  const facturasSeleccionadas = useMemo(
    () =>
      filas
        .map((r) => {
          const n = parseFloat(String(r.aplicar || '').replace(',', '.'));
          if (!Number.isFinite(n) || n <= 0) return null;
          return { ...r, aplicarNum: n };
        })
        .filter(Boolean),
    [filas]
  );

  const tarjetasAntiguedad = useMemo(() => {
    const tramos = ['0-30', '31-60', '61-90', '90+'];
    return tramos.map((tramo) => {
      const enTramo = filas.filter((f) => f.tramo_antiguedad === tramo);
      const suma = enTramo.reduce((s, f) => s + (parseFloat(f.saldo_pendiente) || 0), 0);
      return { tramo, cantidad: enTramo.length, suma };
    });
  }, [filas]);

  const totalBalanceSuplidor = useMemo(
    () => filas.reduce((s, f) => s + (parseFloat(f.saldo_pendiente) || 0), 0),
    [filas]
  );

  const nombreEmpresa =
    selectedCompany?.name ||
    selectedCompany?.empresa ||
    (typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
      ? nombreNegocio.trim()
      : '') ||
    'Empresa';

  const columnasPrint = [
    { id: 'tramo_antiguedad', label: 'Tramo', render: (v) => tramoLabel(v) },
    { id: 'dias_antiguedad', label: 'Días', align: 'center' },
    { id: 'nfactura', label: 'Factura' },
    { id: 'fecha', label: 'Fecha', align: 'center', render: (v) => formatearFecha(v) },
    { id: 'saldo_pendiente', label: 'Saldo pendiente', align: 'right', render: (v) => `$${fmtMoney(v)}` },
  ];

  const enviarAlMotorContablePagoSuplidor = useCallback(
    async (ctxDocumento) => {
      if (!moduloContabilidadInstalado(modulos)) return;
      if (!selectedCompany?.db_empresa) return;
      try {
        const tipoDoc = TIPO_DOCUMENTO_MOTOR_PAGO_SUPLIDORES;
        const reglasRes = await fetch(`${servidor}/contabilidadbackend_listarmotorcontable`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            tipodocumento: tipoDoc,
            ...payloadFormaPagoSiAplica(
              tipoDoc,
              ctxDocumento.formaPago || ctxDocumento.formaPagoRow?.documento
            ),
          }),
        });
        const reglasData = await reglasRes.json().catch(() => ({}));
        if (!reglasRes.ok) {
          showWarning(reglasData.Error || 'No se pudieron consultar las reglas del motor contable');
          return;
        }
        const reglasDoc = reglasMotorDeDocumento(
          reglasData.registros,
          TIPO_DOCUMENTO_MOTOR_PAGO_SUPLIDORES
        );
        if (!documentoMotorDefinido(reglasDoc, TIPO_DOCUMENTO_MOTOR_PAGO_SUPLIDORES)) {
          showWarning(
            'No hay reglas del motor para Pagos a Suplidores; no se registró en diario/histdia'
          );
          return;
        }
        const payloadDocumento = construirDocumentoMotorPagoSuplidor(reglasDoc, ctxDocumento);
        const motorRes = await fetch(`${servidor}/contabilidadbackend_enviarmotorcontable`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            modulos,
            documento: payloadDocumento,
          }),
        });
        const motorData = await motorRes.json().catch(() => ({}));
        if (!motorRes.ok) {
          showWarning(motorData.Error || 'No se pudo enviar el pago al motor contable');
          return;
        }
        if (motorData.omitido) return;
        if (motorData.ok) {
          showSuccess(motorData.mensaje || 'Motor contable: asiento generado');
        } else if (motorData.mensaje) {
          showWarning(motorData.mensaje);
        }
      } catch {
        showWarning('Error de conexión al enviar el pago al motor contable');
      }
    },
    [modulos, servidor, selectedCompany, usuarioLog, claveLog, showWarning, showSuccess]
  );

  const handleGuardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para modificar');
      return;
    }
    if (!suplidorSel?.id) {
      showError('Seleccione un suplidor');
      return;
    }
    if (!formaPago) {
      showError('Seleccione una forma de pago');
      return;
    }
    const montoNum = parseFloat(String(montoPago || '').replace(',', '.'));
    if (!Number.isFinite(montoNum) || montoNum <= 0) {
      showError('Indique un monto de pago válido');
      return;
    }
    if (facturasSeleccionadas.length === 0) {
      showError('Indique monto aplicado en al menos una factura');
      return;
    }
    for (const f of facturasSeleccionadas) {
      const saldo = parseFloat(f.saldo_pendiente) || 0;
      if (f.aplicarNum - saldo > 0.01) {
        showError(`El valor aplicado a factura ${f.nfactura} excede su saldo pendiente`);
        return;
      }
    }
    if (Math.abs((totalAplicar || 0) - montoNum) > 0.01) {
      showError('La suma aplicada en facturas debe ser igual al monto total del pago');
      return;
    }

    const filaFormaPago = formasPagoDb.find((r) => (r.documento || '') === formaPago);
    const tipoDoc = TIPO_DOCUMENTO_MOTOR_PAGO_SUPLIDORES;
    const documentoFormaPago = String(filaFormaPago?.documento || formaPago || '').trim();

    setGuardando(true);
    try {
      const response = await fetch(`${servidor}/contabilidadbackend_guardaraplicacioncobrossuplidor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          suplidor_id: String(suplidorSel.id),
          fecha: fechaPago,
          tipodocumento: tipoDoc,
          ...payloadFormaPagoSiAplica(tipoDoc, documentoFormaPago),
          referencia: referencia.trim(),
          descripcion: descripcion.trim(),
          monto_pago: montoNum,
          detalles: facturasSeleccionadas.map((f) => ({
            nfactura: f.nfactura,
            valor_aplicado: f.aplicarNum,
          })),
          usuario: usuarioLog || '',
          password: claveLog || '',
          modulos,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        showError(data.Error || 'No se pudo aplicar el pago');
        return;
      }
      showSuccess(data.mensaje || 'Pago aplicado correctamente');
      const motorSave = data.motor;
      if (motorSave?.registrado) {
        showSuccess(motorSave.mensaje || 'Motor contable: asiento registrado en diario e histdia');
      } else if (motorSave && !motorSave.omitido && motorSave.mensaje) {
        showWarning(motorSave.mensaje);
      } else if (!motorSave?.registrado) {
        await enviarAlMotorContablePagoSuplidor({
          valorTotal: montoNum,
          formaPago: String(filaFormaPago?.documento || formaPago || '').trim(),
          formaPagoRow: filaFormaPago,
          nfactura: String(facturasSeleccionadas[0]?.nfactura || ''),
          documento: data.documento || DOCUMENTO_PAGO_CXP,
          numero: data.numero || data.id_pago,
          suplidor: String(suplidorSel.id),
          fecha: fechaPago,
          tasa: 1,
          usuario: usuarioLog || '',
          concepto: descripcion.trim() || `Pago a suplidor ${suplidorSel.id}`,
          montos: {
            cuentasporpagar: montoNum,
            descuentos: 0,
            avances: 0,
          },
        });
      }
      setMontoPago('');
      setReferencia('');
      setDescripcion('');
      await cargarFacturasPendientes();
    } catch {
      showError('Error de conexión al guardar aplicación de cobro');
    } finally {
      setGuardando(false);
    }
  };


  const cardShellSx = {
    borderRadius: 2.5,
    border: `1px solid ${FP.color.border}`,
    bgcolor: FP.color.cardBg,
    boxShadow: FP.shadow.soft,
    overflow: 'hidden',
  };

  const filterHeaderSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
  };

  const navyBtnSx = {
    height: 38,
    bgcolor: FP.color.navy,
    color: '#FFFFFF',
    borderRadius: 1.4,
    textTransform: 'none',
    fontWeight: 700,
    boxShadow: 'none',
    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
    '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
  };
  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Aplicación de cobros a suplidor"
          subtitle={
            nombreNegocio ? `${nombreNegocio} · Pagos a facturas pendientes` : 'Pagos a facturas pendientes'
          }
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «aplicacioncobrossuplidor» en roles para aplicar cobros a suplidor.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!puedeOperarDb) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Aplicación de cobros a suplidor"
          subtitle={
            nombreNegocio ? `${nombreNegocio} · Pagos a facturas pendientes` : 'Pagos a facturas pendientes'
          }
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card elevation={0} sx={{ ...cardShellSx, maxWidth: 560, width: '100%', p: { xs: 3, md: 4 } }}>
            <Alert severity="info">
              Seleccione una empresa con base de datos asignada para continuar.
            </Alert>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Aplicación de cobros a suplidor"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Pagos a facturas pendientes`
            : 'Pagos a facturas pendientes'
        }
      />
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PaymentsIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Datos del pago
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Box className="no-print">
              <PrintListado
                title={nombreEmpresa}
                reportTitle="Estado por antigüedad de saldos de suplidor"
                subtitle={suplidorSel ? opcionLabel(suplidorSel) : 'Sin suplidor seleccionado'}
                columns={columnasPrint}
                data={filas}
                emptyMessage="Seleccione un suplidor con facturas pendientes"
                printButtonLabel="Imprimir estado de antigüedad"
                printButtonProps={{
                  disabled: !suplidorSel?.id || filas.length === 0,
                  sx: {
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 1.4,
                    bgcolor: FP.color.navy,
                    color: '#FFFFFF',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  },
                }}
                footer={`Balance total suplidor: $${fmtMoney(totalBalanceSuplidor)}`}
              />
            </Box>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }} className="no-print">
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.92rem', lineHeight: 1.5, mb: 2 }}>
              Registra pagos en pagossup / pagossupres y actualiza factcxp.vplicado.
            </Typography>
            <Grid container spacing={2.5} sx={{ mb: 0 }}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={suplidores}
                  loading={cargandoSupl}
                  getOptionLabel={(o) => opcionLabel(o)}
                  value={suplidorSel}
                  onChange={(_, v) => setSuplidorSel(v)}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Suplidor"
                      size="small"
                      helperText="Obligatorio para aplicar pagos"
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Fecha"
                  value={fechaPago}
                  onChange={(e) => setFechaPago(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Forma de pago"
                  value={formaPago}
                  onChange={(e) => setFormaPago(e.target.value)}
                  disabled={cargandoFormas}
                  SelectProps={{ native: true }}
                  sx={fpInputSx}
                >
                  <option value="">{cargandoFormas ? 'Cargando...' : 'Seleccione'}</option>
                  {formasPagoDb.map((fp) => (
                    <option key={fp.id} value={fp.documento || ''}>
                      {fp.documento || '(sin nombre)'}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Monto pago"
                  value={montoPago}
                  onChange={(e) => setMontoPago(e.target.value)}
                  placeholder="0.00"
                  inputProps={{ inputMode: 'decimal' }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Referencia"
                  value={referencia}
                  onChange={(e) => setReferencia(e.target.value)}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Descripción"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card elevation={0} sx={cardShellSx}>
          <Box sx={filterHeaderSx}>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Facturas pendientes
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>

          {!suplidorSel?.id ? (
            <Alert severity="warning">Seleccione un suplidor para ver facturas pendientes.</Alert>
          ) : cargandoFacturas ? (
            <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : filas.length === 0 ? (
            <Alert severity="info">No hay facturas pendientes para este suplidor.</Alert>
          ) : (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {tarjetasAntiguedad.map(({ tramo, cantidad, suma }) => (
                  <Grid item xs={12} sm={6} md={3} key={tramo}>
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {tramoLabel(tramo)}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        ${fmtMoney(suma)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {cantidad} documento{cantidad !== 1 ? 's' : ''}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Balance total suplidor
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
                  ${fmtMoney(totalBalanceSuplidor)}
                </Typography>
              </Paper>

              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Factura</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell align="center">Tramo</TableCell>
                      <TableCell align="center">Días vencido</TableCell>
                      <TableCell align="right">Saldo pendiente</TableCell>
                      <TableCell align="right">Aplicar</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filas.map((row) => {
                      const dias = parseInt(row.dias_antiguedad || 0, 10);
                      return (
                        <TableRow key={row.keyLocal}>
                          <TableCell>{row.nfactura}</TableCell>
                          <TableCell>{formatearFecha(row.fecha)}</TableCell>
                          <TableCell align="center">{tramoLabel(row.tramo_antiguedad)}</TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              color: dias > 0 ? 'error.main' : 'inherit',
                              fontWeight: dias > 0 ? 700 : 400,
                            }}
                          >
                            {dias}
                          </TableCell>
                          <TableCell align="right">${fmtMoney(row.saldo_pendiente)}</TableCell>
                          <TableCell align="right" sx={{ minWidth: 150 }}>
                            <TextField
                              size="small"
                              value={row.aplicar}
                              onChange={(e) => setAplicarFila(row.keyLocal, e.target.value)}
                              inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
                              placeholder="0.00"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: FP.color.textPrimary }}>
              Total aplicado: ${fmtMoney(totalAplicar)}
            </Typography>
            <Button
              variant="contained"
              onClick={handleGuardar}
              disabled={!puedeModificar || guardando || !suplidorSel?.id}
              sx={navyBtnSx}
            >
              {guardando ? 'Guardando...' : 'Guardar aplicación'}
            </Button>
          </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default AplicacionCobrosSuplidor;
